import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, Box, Typography, Chip, Stack, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useHotkey } from '../../hooks/useHotkeys';

interface HotkeyGroup {
  title: string;
  items: { combo: string; label: string }[];
}

const GLOBAL_HOTKEYS: HotkeyGroup[] = [
  {
    title: 'Navigate',
    items: [
      { combo: 'g d', label: 'Go to Dashboard' },
      { combo: 'g t', label: 'Go to Trades' },
      { combo: 'g n', label: 'Go to Analytics' },
      { combo: 'g a', label: 'Go to Alerts' },
      { combo: 'g s', label: 'Go to Strategies' },
      { combo: 'g c', label: 'Go to Charts' },
      { combo: 'g f', label: 'Go to FNO Dashboard' },
      { combo: 'g i', label: 'Go to Instruments' },
      { combo: 'g ,', label: 'Go to Settings' },
      { combo: 'g h', label: 'Go to Help' },
    ],
  },
  {
    title: 'Global',
    items: [
      { combo: 'Ctrl/⌘ K', label: 'Open command palette' },
      { combo: '?', label: 'Show this hotkey overlay' },
      { combo: 'Esc', label: 'Close dialogs / cancel editing' },
    ],
  },
  {
    title: 'Charts',
    items: [
      { combo: '1–9', label: 'Switch interval (1m, 3m, 5m, 15m, …)' },
      { combo: 'b / c / p', label: 'Toggle Bollinger / AvgCloud / CPR' },
      { combo: 'n b / n s', label: 'Set nBUY / nSHORT trigger' },
      { combo: 'B b / B s', label: 'Set bBUY / bSHORT (breakthrough) trigger' },
    ],
  },
];

const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      fontFamily: 'monospace',
      fontSize: '0.72rem',
      bgcolor: 'action.hover',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 0.75,
      px: 0.75,
      py: 0.25,
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Box>
);

const HotkeyOverlay: React.FC = () => {
  const [open, setOpen] = useState(false);

  useHotkey('?', () => setOpen(true));
  useHotkey('shift+?', () => setOpen(true));

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Keyboard shortcuts</Typography>
        <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {GLOBAL_HOTKEYS.map((group) => (
            <Box key={group.title}>
              <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                {group.title}
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {group.items.map((it) => (
                  <Box key={it.combo} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Kbd>{it.combo}</Kbd>
                    <Typography variant="body2" color="text.secondary">{it.label}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          ))}
          <Chip
            size="small"
            label="Tip: hotkeys are disabled while typing in inputs."
            sx={{ alignSelf: 'flex-start', mt: 1 }}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default HotkeyOverlay;
